/**
 * Auth utility for landing page.
 * Handles one-time token extraction from URL and localStorage management.
 */

import { supabase } from './supabase';

const ACCESS_TOKEN_KEY = 'access_token';
const REFRESH_TOKEN_KEY = 'refresh_token';

/**
 * Call this once on app initialization (e.g., in layout.tsx or a hook).
 * Reads ?token and ?refresh_token from URL if present, stores in localStorage, cleans URL.
 * Returns the current auth tokens (from URL or localStorage).
 */
export function initAuthFromUrl(): { accessToken: string | null; refreshToken: string | null } {
  if (typeof window === 'undefined') return { accessToken: null, refreshToken: null };

  // Read from search params first, then hash (some redirects use hash for sensitive data)
  const urlParams = new URLSearchParams(window.location.search);
  const hashParams = new URLSearchParams(window.location.hash.substring(1));

  const accessToken = urlParams.get('token') ?? hashParams.get('token');
  const refreshToken = urlParams.get('refresh_token') ?? hashParams.get('refresh_token');

  if (accessToken) {
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  }
  if (refreshToken) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
  }

  // Clean both search and hash from URL
  if (accessToken || refreshToken) {
    // Remove token and refresh_token from search params
    urlParams.delete('token');
    urlParams.delete('refresh_token');
    const search = urlParams.toString() ? `?${urlParams.toString()}` : '';
    // Preserve hash (e.g., /#features) if present - only clear token-related hash params
    const hashHasTokens = hashParams.has('token') || hashParams.has('refresh_token');
    const cleanHash = hashHasTokens ? '' : window.location.hash.split('?')[0];
    window.history.replaceState({}, '', window.location.pathname + search + cleanHash);
  }

  return {
    accessToken: localStorage.getItem(ACCESS_TOKEN_KEY),
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY),
  };
}

/**
 * Get the current access token from localStorage.
 */
export function getAccessToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(ACCESS_TOKEN_KEY);
}

/**
 * Get the current refresh token from localStorage.
 */
export function getRefreshToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(REFRESH_TOKEN_KEY);
}

/**
 * Remove the auth tokens (logout).
 *
 * Also clears the landing-page coupon / resolved-coupon / plan keys so that
 * on a shared device, User A's webinar-coupon entitlement doesn't survive
 * User A's logout and bleed into User B's session. Mirrors the React app's
 * AuthContext.tsx SIGNED_IN handler, which already clears the same three
 * keys on the Vite side.
 *
 * Key names match those used in `lib/coupon.ts` (private module constants
 * there — hard-coded here to avoid widening that module's public surface
 * just for a logout teardown).
 */
export function clearTokens(): void {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem("landing_page_coupon");
  localStorage.removeItem("landing_page_resolved_coupon");
  localStorage.removeItem("landing_page_plan");
}

/**
 * Set both tokens in localStorage.
 */
export function setTokens(accessToken: string, refreshToken: string): void {
  localStorage.setItem(ACCESS_TOKEN_KEY, accessToken);
  localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
}

/**
 * Check if user is authenticated using Supabase session.
 */
export async function isAuthenticatedAsync(): Promise<boolean> {
  // First check our custom token (set from URL param)
  const accessToken = getAccessToken();
  if (accessToken) return true;

  // Also check Supabase's session
  const { data } = await supabase.auth.getSession();
  return !!data.session;
}

/**
 * Check if user is authenticated (sync version - checks custom token only).
 */
export function isAuthenticated(): boolean {
  return !!getAccessToken();
}

/**
 * Decode user_id (sub claim) from the JWT access token stored in localStorage.
 * Note: This decodes the payload WITHOUT signature verification — suitable for
 * reading claims from a token that was already validated by the backend.
 */
export function decodeUserIdFromToken(): string | null {
  const token = getAccessToken();
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.sub ?? null;
  } catch {
    return null;
  }
}

/**
 * Check if the auth token is expired.
 * Returns true if expired or not present.
 */
export function isTokenExpired(): boolean {
  const token = getAccessToken();
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp ?? null;
    if (!exp) return false; // No exp claim, assume valid
    return Date.now() / 1000 > exp;
  } catch {
    return true; // Invalid token format, treat as expired
  }
}

/**
 * Refresh the access token using the stored refresh token.
 * Returns new access token on success, null on failure.
 */
export async function refreshAccessToken(): Promise<string | null> {
  const refreshToken = getRefreshToken();
  if (!refreshToken) return null;

  try {
    const { data, error } = await supabase.auth.refreshSession({
      refresh_token: refreshToken,
    });

    if (error || !data.session) {
      console.warn('Failed to refresh access token:', error?.message);
      return null;
    }

    // Update stored tokens
    setTokens(data.session.access_token, data.session.refresh_token);
    return data.session.access_token;
  } catch (err) {
    console.warn('Exception during token refresh:', err);
    return null;
  }
}
